import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import type { Practice, Squad, SquadPlayer, Round, PlayerRound, Shot, ShotResult, Trajectory, Discipline, DisciplineVariant } from '../types';
import { useProfile } from './ProfileContext';
import { getDisciplineConfig } from '../config/disciplines';
import {
  getShootingPosition,
  getTargetsPerRound,
  getDoublesShots,
  type DoublesButtonType,
} from '../utils/scoringEngine';
import {
  createPractice as dbCreatePractice,
  createSquad as dbCreateSquad,
  addPlayerToSquad as dbAddPlayerToSquad,
  createRound as dbCreateRound,
  createPlayerRound as dbCreatePlayerRound,
  upsertQueuedShots,
  endPractice as dbEndPractice,
  getSquadPlayers,
  getPlayerRoundsByRound,
  getShotsByPlayerRound,
  deleteShotById,
  getRoundsBySquad,
  getSquadsByPractice,
  getEventById,
} from '../db/supabase-operations';
import {
  getQueuedScoreCount,
  getQueuedScoreWrites,
  markQueuedScoreFailed,
  queueScoreWrite,
  removeQueuedScoreWrite,
} from '../db/scoreQueue';
import { markUsefulAction } from '../utils/appEngagement';

interface PracticeContextType {
  isRestoringPractice: boolean;
  activePractice: Practice | null;
  activeSquad: Squad | null;
  squadPlayers: SquadPlayer[];
  activeRound: Round | null;
  allRounds: Round[];
  playerRounds: PlayerRound[];
  currentPlayerRoundIndex: number;
  shotCountsByPlayerRound: Record<string, number>;
  roundShots: Shot[];
  pendingShotCount: number;
  scoringPersistenceError: string | null;
  retryPendingShots: () => Promise<void>;
  startPractice: (teamId: string, coachId: string, name?: string, isLiveEvent?: boolean, discipline?: Discipline, variant?: DisciplineVariant, location?: string, scheduledStart?: string, defaultHandicapYardage?: number) => Promise<Practice>;
  resumePractice: (practice: Practice) => Promise<void>;
  createSquad: (practiceId: string, name: string, playerStations: Record<string, number>, handicapYardages?: Record<string, number>) => Promise<Squad>;
  addRound: (squadId: string, roundNumber: number, shotsPerStation?: number) => Promise<Round>;
  recordShot: (result: ShotResult, trajectory: Trajectory) => Promise<Shot | void>;
  recordDoublesShot: (buttonType: DoublesButtonType, trajectory: Trajectory) => Promise<void>;
  undoLastShot: () => Promise<{ shots: Shot[]; playerRoundIndex: number } | void>;
  endCurrentPractice: () => Promise<void>;
  loadSquadPlayers: (squadId?: string) => Promise<void>;
  loadRounds: (squadId?: string) => Promise<void>;
  setActiveRound: (round: Round) => void;
  resetForNewRound: () => void;
}

const PracticeContext = createContext<PracticeContextType | undefined>(undefined);
const ACTIVE_EVENT_KEY = 'deadbird-active-event';
const ACTIVE_EVENT_SNAPSHOT_KEY = 'deadbird-active-event-snapshot';

interface ActiveEventSnapshot {
  version: 1;
  practice: Practice;
  squad: Squad | null;
  squadPlayers: SquadPlayer[];
  round: Round | null;
  allRounds: Round[];
  playerRounds: PlayerRound[];
  currentPlayerRoundIndex: number;
  shotCountsByPlayerRound: Record<string, number>;
  roundShots?: Shot[];
}

export const PracticeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { activeProfile } = useProfile();
  const [isRestoringPractice, setIsRestoringPractice] = useState(
    () => Boolean(localStorage.getItem(ACTIVE_EVENT_KEY))
  );
  const [activePractice, setActivePractice] = useState<Practice | null>(null);
  const [activeSquad, setActiveSquad] = useState<Squad | null>(null);
  const [squadPlayers, setSquadPlayers] = useState<SquadPlayer[]>([]);
  const [activeRound, setActiveRound] = useState<Round | null>(null);
  const [allRounds, setAllRounds] = useState<Round[]>([]);
  const [playerRounds, setPlayerRounds] = useState<PlayerRound[]>([]);
  const [currentPlayerRoundIndex, setCurrentPlayerRoundIndex] = useState(0);
  const [shotCountsByPlayerRound, setShotCountsByPlayerRound] = useState<Record<string, number>>({});
  const [roundShots, setRoundShots] = useState<Shot[]>([]);
  const [pendingShotCount, setPendingShotCount] = useState(0);
  const [scoringPersistenceError, setScoringPersistenceError] = useState<string | null>(null);
  const shotCountsRef = useRef<Record<string, number>>({});
  const currentPlayerRoundIndexRef = useRef(0);
  const syncInFlightRef = useRef<Promise<void> | null>(null);
  const restoringEventRef = useRef(false);
  const lastShotTimestampRef = useRef(0);
  const persistedScoreHistoryRef = useRef<Array<{ shots: Shot[]; playerRoundIndex: number; queueId?: string }>>([]);

  const setCurrentIndex = (index: number) => {
    currentPlayerRoundIndexRef.current = index;
    setCurrentPlayerRoundIndex(index);
  };

  const replaceShotCounts = (counts: Record<string, number>) => {
    shotCountsRef.current = counts;
    setShotCountsByPlayerRound(counts);
  };

  const resetScoringSession = () => {
    setRoundShots([]);
    setScoringPersistenceError(null);
    lastShotTimestampRef.current = 0;
    persistedScoreHistoryRef.current = [];
  };

  const refreshPendingShotCount = async (eventId = activePractice?.id) => {
    setPendingShotCount(await getQueuedScoreCount(eventId));
  };

  const retryPendingShots = async (): Promise<void> => {
    if (syncInFlightRef.current) return syncInFlightRef.current;
    if (!navigator.onLine) {
      await refreshPendingShotCount();
      return;
    }

    const syncOperation = (async () => {
      let syncFailed = false;
      while (!syncFailed) {
        const queuedWrites = await getQueuedScoreWrites(activePractice?.id);
        if (queuedWrites.length === 0) break;
        for (const queuedWrite of queuedWrites) {
          try {
            await upsertQueuedShots(queuedWrite.shots);
            await removeQueuedScoreWrite(queuedWrite.id);
            setScoringPersistenceError(null);
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Scores need attention.';
            await markQueuedScoreFailed(queuedWrite.id, message);
            setScoringPersistenceError(message);
            syncFailed = true;
            break;
          }
        }
      }
      await refreshPendingShotCount();
    })();

    syncInFlightRef.current = syncOperation;
    try {
      await syncOperation;
    } finally {
      syncInFlightRef.current = null;
    }
  };

  const persistScoreWrite = async (queueId: string, practiceId: string, shots: Shot[]): Promise<void> => {
    await queueScoreWrite({ id: queueId, eventId: practiceId, shots, createdAt: shots[0]?.timestamp || Date.now() });
    await refreshPendingShotCount(practiceId);
    if (navigator.onLine) {
      void retryPendingShots().then(() => retryPendingShots());
    }
  };

  const flushShotWrites = async () => {
    await retryPendingShots();
    const remaining = await getQueuedScoreCount(activePractice?.id);
    if (remaining > 0) throw new Error(`${remaining} queued ${remaining === 1 ? 'score still needs' : 'scores still need'} to sync.`);
  };

  useEffect(() => {
    const handleOnline = () => { void retryPendingShots(); };
    window.addEventListener('online', handleOnline);
    void refreshPendingShotCount();
    if (navigator.onLine) void retryPendingShots();
    return () => window.removeEventListener('online', handleOnline);
  }, [activePractice?.id]);

  const startPractice = async (
    teamId: string,
    coachId: string,
    name?: string,
    isLiveEvent: boolean = false,
    discipline: Discipline = 'trap',
    variant: DisciplineVariant = 'american-trap',
    location?: string,
    scheduledStart?: string,
    defaultHandicapYardage?: number
  ): Promise<Practice> => {
    if (!activeProfile) {
      throw new Error('No active profile');
    }
    const practice = await dbCreatePractice(teamId, coachId, name, isLiveEvent, activeProfile.id, discipline, variant, location, scheduledStart, defaultHandicapYardage);
    setActivePractice(practice);
    setActiveSquad(null);
    setSquadPlayers([]);
    setActiveRound(null);
    setAllRounds([]);
    setPlayerRounds([]);
    setCurrentIndex(0);
    replaceShotCounts({});
    resetScoringSession();
    localStorage.setItem(ACTIVE_EVENT_KEY, practice.id);
    return practice;
  };

  const resumePractice = async (practice: Practice): Promise<void> => {
    const squads = await getSquadsByPractice(practice.id);
    const squad = squads[0] || null;
    const loadedSquadPlayers = squad ? await getSquadPlayers(squad.id) : [];
    const loadedRounds = squad ? await getRoundsBySquad(squad.id) : [];
    const round = loadedRounds.length > 0 ? loadedRounds[loadedRounds.length - 1] : null;
    let loadedPlayerRounds: PlayerRound[] = [];
    let shotSets: Shot[][] = [];

    if (round) {
      loadedPlayerRounds = await getPlayerRoundsByRound(round.id);
      loadedPlayerRounds.sort((a, b) => {
        const stationA = loadedSquadPlayers.find(player => player.id === a.squadPlayerId)?.station || 0;
        const stationB = loadedSquadPlayers.find(player => player.id === b.squadPlayerId)?.station || 0;
        return stationA - stationB;
      });
      shotSets = await Promise.all(loadedPlayerRounds.map(playerRound => getShotsByPlayerRound(playerRound.id)));
    }

    const queuedWrites = await getQueuedScoreWrites(practice.id);
    const remoteMutationIds = new Set(shotSets.flat().map(shot => shot.clientMutationId || shot.id));
    for (const queuedWrite of queuedWrites) {
      for (const shot of queuedWrite.shots) {
        if (remoteMutationIds.has(shot.clientMutationId || shot.id)) continue;
        const playerRoundIndex = loadedPlayerRounds.findIndex(playerRound => playerRound.id === shot.playerRoundId);
        if (playerRoundIndex >= 0) shotSets[playerRoundIndex] = [...(shotSets[playerRoundIndex] || []), shot];
      }
    }

    const loadedShotCounts = Object.fromEntries(
      loadedPlayerRounds.map((playerRound, index) => [playerRound.id, shotSets[index]?.length || 0])
    );
    const minimumShotCount = loadedPlayerRounds.length > 0
      ? Math.min(...loadedPlayerRounds.map(playerRound => loadedShotCounts[playerRound.id] || 0))
      : 0;
    const nextPlayerIndex = Math.max(0, loadedPlayerRounds.findIndex(
      playerRound => (loadedShotCounts[playerRound.id] || 0) === minimumShotCount
    ));

    resetScoringSession();
    setActivePractice(practice);
    setActiveSquad(squad);
    setSquadPlayers(loadedSquadPlayers);
    setAllRounds(loadedRounds);
    setActiveRound(round);
    setPlayerRounds(loadedPlayerRounds);
    replaceShotCounts(loadedShotCounts);
    setRoundShots(shotSets.flat());
    setCurrentIndex(nextPlayerIndex);
    const remoteHistory = shotSets
      .flatMap((shots, index) => shots.map(shot => ({ shots: [shot], playerRoundIndex: index })))
      .sort((a, b) => a.shots[0].timestamp - b.shots[0].timestamp);
    const queuedIds = new Set(queuedWrites.flatMap(write => write.shots.map(shot => shot.id)));
    persistedScoreHistoryRef.current = [
      ...remoteHistory.filter(entry => !queuedIds.has(entry.shots[0].id)),
      ...queuedWrites.map(write => ({
        shots: write.shots,
        playerRoundIndex: loadedPlayerRounds.findIndex(playerRound => playerRound.id === write.shots[0]?.playerRoundId),
        queueId: write.id,
      })).filter(entry => entry.playerRoundIndex >= 0),
    ].sort((first, second) => first.shots[0].timestamp - second.shots[0].timestamp);
    localStorage.setItem(ACTIVE_EVENT_KEY, practice.id);
    await refreshPendingShotCount(practice.id);
    if (navigator.onLine) void retryPendingShots();
  };

  const restorePracticeSnapshot = async (eventId: string): Promise<boolean> => {
    const rawSnapshot = localStorage.getItem(ACTIVE_EVENT_SNAPSHOT_KEY);
    if (!rawSnapshot) return false;
    try {
      const snapshot = JSON.parse(rawSnapshot) as ActiveEventSnapshot;
      if (snapshot.version !== 1 || snapshot.practice.id !== eventId || snapshot.practice.status !== 'active') return false;
      const queuedWrites = await getQueuedScoreWrites(eventId);
      resetScoringSession();
      const restoredShots = new Map((snapshot.roundShots || []).map(shot => [shot.id, shot]));
      for (const write of queuedWrites) {
        for (const shot of write.shots) {
          if (snapshot.playerRounds.some(round => round.id === shot.playerRoundId)) restoredShots.set(shot.id, shot);
        }
      }
      setRoundShots([...restoredShots.values()]);
      setActivePractice(snapshot.practice);
      setActiveSquad(snapshot.squad);
      setSquadPlayers(snapshot.squadPlayers);
      setActiveRound(snapshot.round);
      setAllRounds(snapshot.allRounds);
      setPlayerRounds(snapshot.playerRounds);
      replaceShotCounts(snapshot.shotCountsByPlayerRound);
      setCurrentIndex(snapshot.currentPlayerRoundIndex);
      persistedScoreHistoryRef.current = queuedWrites.map(write => ({
        shots: write.shots,
        playerRoundIndex: snapshot.playerRounds.findIndex(
          playerRound => playerRound.id === write.shots[0]?.playerRoundId
        ),
        queueId: write.id,
      })).filter(entry => entry.playerRoundIndex >= 0);
      await refreshPendingShotCount(eventId);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const eventId = localStorage.getItem(ACTIVE_EVENT_KEY);
    if (!eventId) {
      setIsRestoringPractice(false);
      return;
    }
    if (!activeProfile || activePractice || restoringEventRef.current) return;
    restoringEventRef.current = true;
    setIsRestoringPractice(true);
    void (async () => {
      if (!navigator.onLine && await restorePracticeSnapshot(eventId)) return;
      try {
        const event = await getEventById(eventId);
        if (event?.status === 'active') {
          await resumePractice(event);
          return;
        }
        localStorage.removeItem(ACTIVE_EVENT_KEY);
        localStorage.removeItem(ACTIVE_EVENT_SNAPSHOT_KEY);
      } catch {
        await restorePracticeSnapshot(eventId);
      }
    })().finally(() => {
      restoringEventRef.current = false;
      setIsRestoringPractice(false);
    });
  }, [activeProfile?.id, activePractice?.id]);

  useEffect(() => {
    if (!activePractice) return;
    const snapshot: ActiveEventSnapshot = {
      version: 1,
      practice: activePractice,
      squad: activeSquad,
      squadPlayers,
      round: activeRound,
      allRounds,
      playerRounds,
      currentPlayerRoundIndex,
      shotCountsByPlayerRound,
      roundShots,
    };
    localStorage.setItem(ACTIVE_EVENT_SNAPSHOT_KEY, JSON.stringify(snapshot));
  }, [
    activePractice,
    activeSquad,
    squadPlayers,
    activeRound,
    allRounds,
    playerRounds,
    currentPlayerRoundIndex,
    shotCountsByPlayerRound,
    roundShots,
  ]);

  const createSquad = async (
    practiceId: string,
    name: string,
    playerStations: Record<string, number>,
    handicapYardages?: Record<string, number>
  ): Promise<Squad> => {
    const squad = await dbCreateSquad(practiceId, name);

    // Add all players to the squad with their stations and optional handicap yardage
    for (const [playerId, station] of Object.entries(playerStations)) {
      const yardage = handicapYardages?.[playerId];
      await dbAddPlayerToSquad(squad.id, playerId, station, yardage);
    }

    setActiveSquad(squad);
    await loadSquadPlayers(squad.id);
    return squad;
  };

  const loadSquadPlayers = async (squadId?: string): Promise<void> => {
    const targetSquadId = squadId || activeSquad?.id;
    if (!targetSquadId) return;
    const players = await getSquadPlayers(targetSquadId);
    setSquadPlayers(players);
  };

  const loadRounds = async (squadId?: string): Promise<void> => {
    const targetSquadId = squadId || activeSquad?.id;
    if (!targetSquadId) return;
    const rounds = await getRoundsBySquad(targetSquadId);
    setAllRounds(rounds);
  };

  const addRound = async (squadId: string, roundNumber: number, shotsPerStation: number = 5): Promise<Round> => {
    await flushShotWrites();
    const config = activePractice ? getDisciplineConfig(activePractice.variant) : undefined;
    const fixedShots = config?.discipline === 'trap' ? config.shotsPerStation : shotsPerStation;
    const round = await dbCreateRound(squadId, roundNumber, fixedShots);

    // Create PlayerRounds for all squad players, sorted by station
    const players = await getSquadPlayers(squadId);
    // Sort by station to ensure squad leader (station 1) shoots first
    players.sort((a, b) => a.station - b.station);

    for (const sp of players) {
      await dbCreatePlayerRound(round.id, sp.id, sp.station);
    }

    // Load player rounds for this round and sort by station order
    const prs = await getPlayerRoundsByRound(round.id);
    // Sort playerRounds by their squad player's station
    const sortedPrs = prs.sort((a, b) => {
      const spA = players.find(p => p.id === a.squadPlayerId);
      const spB = players.find(p => p.id === b.squadPlayerId);
      return (spA?.station || 0) - (spB?.station || 0);
    });

    setActiveRound(round);
    setAllRounds(prev => [...prev.filter(existing => existing.id !== round.id), round]
      .sort((a, b) => a.roundNumber - b.roundNumber));
    setPlayerRounds(sortedPrs);
    setCurrentIndex(0);
    replaceShotCounts(Object.fromEntries(sortedPrs.map(playerRound => [playerRound.id, 0])));
    resetScoringSession();

    return round;
  };

  const recordShot = (result: ShotResult, trajectory: Trajectory): Promise<Shot | void> => {
    if (!activePractice || !activeRound || playerRounds.length === 0) return Promise.resolve();

    const currentIndex = currentPlayerRoundIndexRef.current;
    const currentPlayerRound = playerRounds[currentIndex];
    const squadPlayer = squadPlayers.find(sp => sp.id === currentPlayerRound.squadPlayerId);
    if (!squadPlayer) return Promise.resolve();

    const config = activePractice ? getDisciplineConfig(activePractice.variant) : undefined;
    const stationCount = config?.stationCount || 5;

    const totalShots = shotCountsRef.current[currentPlayerRound.id] || 0;
    const variant = activePractice?.variant || 'american-trap';
    const targetsPerRound = getTargetsPerRound(variant, activeRound.shotsPerStation, stationCount);
    if (totalShots >= targetsPerRound) return Promise.resolve();

    const position = getShootingPosition(
      variant,
      totalShots,
      activeRound.shotsPerStation,
      squadPlayer.station,
      stationCount
    );

    if (position.isDoubles) {
      return Promise.reject(new Error('A doubles position must be recorded as a pair'));
    }

    const timestamp = Math.max(Date.now(), lastShotTimestampRef.current + 1);
    lastShotTimestampRef.current = timestamp;
    const newTotalShots = totalShots + 1;
    replaceShotCounts({ ...shotCountsRef.current, [currentPlayerRound.id]: newTotalShots });

    if (currentIndex < playerRounds.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (newTotalShots < targetsPerRound) {
      setCurrentIndex(0);
    }

    const shotId = crypto.randomUUID();
    const queueId = crypto.randomUUID();
    const localShot: Shot = {
      id: shotId,
      clientMutationId: shotId,
      playerRoundId: currentPlayerRound.id,
      station: position.station,
      shotNumber: position.shotNumber,
      result,
      trajectory,
      timestamp,
    };
    setRoundShots(shots => [...shots, localShot]);
    return persistScoreWrite(queueId, activePractice.id, [localShot]).then(() => {
      persistedScoreHistoryRef.current.push({ shots: [localShot], playerRoundIndex: currentIndex, queueId });
      return localShot;
    }).catch(error => {
      setRoundShots(shots => shots.filter(shot => shot.id !== localShot.id));
      replaceShotCounts({ ...shotCountsRef.current, [currentPlayerRound.id]: totalShots });
      setCurrentIndex(currentIndex);
      setScoringPersistenceError('This device could not queue the score safely.');
      throw error;
    });
  };

  const recordDoublesShot = (buttonType: DoublesButtonType, trajectory: Trajectory): Promise<void> => {
    if (!activePractice || !activeRound || playerRounds.length === 0) return Promise.resolve();

    const currentIndex = currentPlayerRoundIndexRef.current;
    const currentPlayerRound = playerRounds[currentIndex];
    const squadPlayer = squadPlayers.find(sp => sp.id === currentPlayerRound.squadPlayerId);
    if (!squadPlayer) return Promise.resolve();

    const config = activePractice ? getDisciplineConfig(activePractice.variant) : undefined;
    const stationCount = config?.stationCount || 5;

    const totalShots = shotCountsRef.current[currentPlayerRound.id] || 0;
    const variant = activePractice?.variant || 'ata-doubles';
    const targetsPerRound = getTargetsPerRound(variant, activeRound.shotsPerStation, stationCount);
    if (totalShots + 2 > targetsPerRound) return Promise.resolve();

    const position = getShootingPosition(
      variant,
      totalShots,
      activeRound.shotsPerStation,
      squadPlayer.station,
      stationCount
    );

    if (!position.isDoubles) {
      return Promise.reject(new Error('A singles position cannot be recorded as a pair'));
    }

    const doublesResults = getDoublesShots(buttonType);

    const timestamp = Math.max(Date.now(), lastShotTimestampRef.current + 1);
    lastShotTimestampRef.current = timestamp + 1;
    const newTotalShots = totalShots + 2;
    replaceShotCounts({ ...shotCountsRef.current, [currentPlayerRound.id]: newTotalShots });

    if (currentIndex < playerRounds.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (newTotalShots < targetsPerRound) {
      setCurrentIndex(0);
    }

    const queueId = crypto.randomUUID();
    const localShots: Shot[] = doublesResults.map((doubleResult, index) => {
      const shotId = crypto.randomUUID();
      return {
        id: shotId,
        clientMutationId: shotId,
        playerRoundId: currentPlayerRound.id,
        station: position.station,
        shotNumber: position.shotNumber + index,
        result: doubleResult.result,
        trajectory,
        timestamp: timestamp + index,
      };
    });
    setRoundShots(shots => [...shots, ...localShots]);
    return persistScoreWrite(queueId, activePractice.id, localShots).then(() => {
      persistedScoreHistoryRef.current.push({ shots: localShots, playerRoundIndex: currentIndex, queueId });
    }).catch(error => {
      setRoundShots(shots => shots.filter(shot => !localShots.some(local => local.id === shot.id)));
      replaceShotCounts({ ...shotCountsRef.current, [currentPlayerRound.id]: totalShots });
      setCurrentIndex(currentIndex);
      setScoringPersistenceError('This device could not queue the score safely.');
      throw error;
    });
  };

  const undoLastShot = async (): Promise<{ shots: Shot[]; playerRoundIndex: number } | void> => {
    if (playerRounds.length === 0) return;
    if (syncInFlightRef.current) await syncInFlightRef.current;

    const lastScore = persistedScoreHistoryRef.current.pop();
    if (lastScore) {
      const queuedWrites = await getQueuedScoreWrites(activePractice?.id);
      const isQueued = !!lastScore.queueId && queuedWrites.some(write => write.id === lastScore.queueId);
      if (isQueued && lastScore.queueId) {
        await removeQueuedScoreWrite(lastScore.queueId);
      } else {
        for (const shot of [...lastScore.shots].reverse()) await deleteShotById(shot.id);
      }
      const playerRoundId = playerRounds[lastScore.playerRoundIndex].id;
      setRoundShots(shots => shots.filter(shot => !lastScore.shots.some(removed => removed.id === shot.id)));
      replaceShotCounts({
        ...shotCountsRef.current,
        [playerRoundId]: Math.max(0, (shotCountsRef.current[playerRoundId] || 0) - lastScore.shots.length),
      });
      setCurrentIndex(lastScore.playerRoundIndex);
      setScoringPersistenceError(null);
      await refreshPendingShotCount();
      return { shots: lastScore.shots, playerRoundIndex: lastScore.playerRoundIndex };
    }
  };

  const endCurrentPractice = async (): Promise<void> => {
    if (!activePractice) return;
    await flushShotWrites();
    await dbEndPractice(activePractice.id, activeProfile?.id);
    setActivePractice(null);
    setActiveSquad(null);
    setSquadPlayers([]);
    setActiveRound(null);
    setAllRounds([]);
    setPlayerRounds([]);
    setCurrentIndex(0);
    replaceShotCounts({});
    resetScoringSession();
    setPendingShotCount(0);
    localStorage.removeItem(ACTIVE_EVENT_KEY);
    localStorage.removeItem(ACTIVE_EVENT_SNAPSHOT_KEY);
    markUsefulAction();
  };

  const resetForNewRound = () => {
    setCurrentIndex(0);
  };

  return (
    <PracticeContext.Provider
      value={{
        isRestoringPractice,
        activePractice,
        activeSquad,
        squadPlayers,
        activeRound,
        allRounds,
        playerRounds,
        currentPlayerRoundIndex,
        shotCountsByPlayerRound,
        roundShots,
        pendingShotCount,
        scoringPersistenceError,
        retryPendingShots,
        startPractice,
        resumePractice,
        createSquad,
        addRound,
        recordShot,
        recordDoublesShot,
        undoLastShot,
        endCurrentPractice,
        loadSquadPlayers,
        loadRounds,
        setActiveRound,
        resetForNewRound,
      }}
    >
      {children}
    </PracticeContext.Provider>
  );
};

export const usePractice = (): PracticeContextType => {
  const context = useContext(PracticeContext);
  if (!context) {
    throw new Error('usePractice must be used within PracticeProvider');
  }
  return context;
};
